import { useCallback } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MCPFunction, useListMCPFunctions } from "@/queries/evals/useListMCPFunctions";

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error as any).message;
  }

  return "Failed to load functions";
};

interface FunctionDropdownProps {
  serverUrl: string;
  headers: Record<string, string>;
  selectedFunction: string | null;
  onFunctionSelect: (mcpFunction: MCPFunction) => void;
  disabled?: boolean;
}

const FunctionDropdown = ({
  serverUrl,
  headers,
  selectedFunction,
  onFunctionSelect,
  disabled = false,
}: FunctionDropdownProps) => {
  const result = useListMCPFunctions(serverUrl, headers);

  const handleValueChange = useCallback(
    (value: string) => {
      const mcpFunction = result.data?.find(f => f.name === value);
      if (mcpFunction) {
        onFunctionSelect(mcpFunction);
      }
    },
    [result.data, onFunctionSelect],
  );

  const isDisabled = disabled || result.isLoading || result.isFetching;

  return (
    <div className="w-full">
      <Select
        value={selectedFunction ?? undefined}
        onValueChange={handleValueChange}
        disabled={isDisabled}
      >
        <SelectTrigger className="w-full">
          <div className="flex w-full items-center justify-between">
            <SelectValue placeholder="Select a Function..." />
            {(result.isLoading || result.isFetching) && <LoadingSpinner size={5} />}
          </div>
        </SelectTrigger>
        <SelectContent>
          {result.isSuccess &&
            result.data.map(mcpFunction => (
              <SelectItem key={mcpFunction.name} value={mcpFunction.name}>
                {mcpFunction.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      {result.isError && (
        <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
          <span>{getErrorMessage(result.error)}</span>
        </div>
      )}
    </div>
  );
};

export default FunctionDropdown;
